import Badge from 'components/Badge/Badge'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import { money, signedMoney } from '../report/pnlCharts'
import { useT } from '../hooks'
import BBookEmpty from './BBookEmpty'
import BBookInfo from './BBookInfo'
import BBookKpi from './BBookKpi'

const SIDE_BADGE = { LONG: '#0F766E', SHORT: '#C2410C' }

// One line per flow, the amount on the right: what the house earned or paid, with the count behind it when there is one.
const Lines = ({ rows, t }) => (
  <dl className='b_book_ledger_lines'>
    {rows.map(({ key, value, count, signed }) => (
      <div key={key} className='b_book_ledger_line'>
        <dt>{t(`pages.b_book_pnl.ledger.lines.${key}`)}{count ? <span className='b_book_hint'> · {count}</span> : null}</dt>
        <dd className='b_book_num'>{signed ? signedMoney(value) : money(value)}</dd>
      </div>
    ))}
  </dl>
)

// The house's statement for the period, three figures first: received, paid, net. The lines behind them and the margin tied up
// on LMAX sit in a fold that opens on demand, so the page reads like the old one unless someone wants the detail.
const BBookLedger = ({ ledger, zone }) => {
  const { t } = useT()
  if (!ledger) return null
  const { received, paid, locked } = ledger
  const stamp = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { timeZone: zone, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—')

  const movementRows = locked.movements.map((row) => ({
    data: [
      <TickerImage ticker={row.ticker} />,
      <Badge text={row.account.toUpperCase()} color={SIDE_BADGE[row.account.toUpperCase()]} />,
      <span className='b_book_num'>{row.quantity}</span>,
      <span className='b_book_num'>{money(row.funded)}</span>,
      stamp(row.since),
      row.released_at ? stamp(row.released_at) : <strong>{t('pages.b_book_pnl.ledger.still_locked')}</strong>,
      <span className='b_book_num'>{row.days}</span>,
    ],
    utils: { id: row.id },
  }))

  return (
    <>
      <div className='b_book_kpis'>
        <BBookKpi label={t('pages.b_book_pnl.ledger.received')} value={received.total} format='signed' info={t('pages.b_book_info.ledger_received')} />
        <BBookKpi label={t('pages.b_book_pnl.ledger.paid')} value={paid.total} info={t('pages.b_book_info.ledger_paid')} />
        <BBookKpi label={t('pages.b_book_pnl.ledger.net')} value={received.total - paid.total} format='signed' info={t('pages.b_book_info.ledger_net')}
          hint={locked.open > 0 ? t('pages.b_book_pnl.ledger.locked_short', { amount: money(locked.open) }) : null} />
      </div>

      <details className='b_book_details'>
        <summary>{t('pages.b_book_pnl.ledger.details')}</summary>
        <div className='b_book_report_grid'>
          <div className='b_book_report_card'>
            <BBookInfo label={t('pages.b_book_pnl.ledger.received')} text={t('pages.b_book_info.ledger_received')} />
            <h3>{t('pages.b_book_pnl.ledger.received')}</h3>
            <Lines t={t} rows={[
              { key: 'commissions', value: received.commissions },
              { key: 'spread', value: received.spread },
              { key: 'overnight', value: received.overnight },
              { key: 'realized', value: received.realized, signed: true },
            ]} />
          </div>
          <div className='b_book_report_card'>
            <BBookInfo label={t('pages.b_book_pnl.ledger.paid')} text={t('pages.b_book_info.ledger_paid')} />
            <h3>{t('pages.b_book_pnl.ledger.paid')}</h3>
            <Lines t={t} rows={[
              { key: 'lmax_commissions', value: paid.lmax_commissions, count: t('pages.b_book_pnl.ledger.hedges', { count: paid.hedges }) + (paid.hedges_unpriced ? ` (${t('pages.b_book_pnl.ledger.unpriced', { count: paid.hedges_unpriced })})` : '') },
              { key: 'lmax_overnight', value: paid.lmax_overnight, count: t('pages.b_book_pnl.ledger.nights', { count: paid.nights }) },
              { key: 'pairing_slippage', value: paid.pairing_slippage, signed: true },
              { key: 'beyond_deposit', value: paid.beyond_deposit, count: t('pages.b_book_pnl.ledger.closes', { count: paid.beyond_deposit_closes }) },
            ]} />
            <p className='b_book_hint'>{t('pages.b_book_pnl.ledger.paid_hint')}</p>
          </div>
          <div className='b_book_report_card'>
            <BBookInfo label={t('pages.b_book_pnl.ledger.locked')} text={t('pages.b_book_info.ledger_locked')} />
            <h3>{t('pages.b_book_pnl.ledger.locked')}</h3>
            <p className='b_book_kpi_value b_book_num'>{money(locked.open)}</p>
            <p className='b_book_hint'>{t('pages.b_book_pnl.ledger.locked_hint', { period: money(locked.period), euro_days: money(locked.euro_days) })}</p>
          </div>
          {movementRows.length > 0 && (
            <div className='b_book_report_card b_book_report_card_full'>
              <h3>{t('pages.b_book_pnl.ledger.movements_title', { count: movementRows.length })}</h3>
              <Tableau thead={['instrument', 'side', 'quantity', 'funded', 'since', 'released', 'days'].map((key) => t(`pages.b_book_pnl.ledger.table.${key}`))} tbody={movementRows} />
            </div>
          )}
          {movementRows.length === 0 && <div className='b_book_report_card b_book_report_card_full'><BBookEmpty title={t('pages.b_book_pnl.ledger.empty')} hint={t('pages.b_book_pnl.ledger.empty_hint')} /></div>}
        </div>
      </details>
    </>
  )
}

export default BBookLedger
